/**
 * Bar chart showing the distribution of findings by severity level.
 * Uses Recharts for visualization.
 */

import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import type { FindingSummary } from '@/types/finding';
import type { Severity } from '@/types/finding';

interface SeverityChartProps {
  summary: FindingSummary | null;
}

/** Map severity to chart bar colors. */
const severityColors: Record<Severity, string> = {
  critical: '#dc2626',
  high: '#ea580c',
  medium: '#ca8a04',
  low: '#2563eb',
  info: '#6b7280',
};

export default function SeverityChart({ summary }: SeverityChartProps) {
  if (!summary) return null;

  const data = (['critical', 'high', 'medium', 'low', 'info'] as Severity[]).map((sev) => ({
    name: sev.charAt(0).toUpperCase() + sev.slice(1),
    count: summary.by_severity[sev] ?? 0,
    severity: sev,
  }));

  return (
    <div className="card">
      <h3 className="mb-4 text-sm font-semibold text-gray-700">Findings by Severity</h3>
      <ResponsiveContainer width="100%" height={250}>
        <BarChart data={data} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis dataKey="name" tick={{ fontSize: 12 }} />
          <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
          <Tooltip />
          <Bar dataKey="count" radius={[4, 4, 0, 0]}>
            {data.map((entry) => (
              <Cell key={entry.severity} fill={severityColors[entry.severity]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
